import React, { useState, useEffect } from 'react';
import { FiMap, FiX, FiPlus, FiGlobe } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import BaseUrl from '../../../Url/BaseUrl';

const AddCityVillage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    cityVillageCode: '',
    cityVillageNameEn: '',
    cityVillageNameHi: '',
    stateCode: '',
    districtCode: '',
    status: true
  });

  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStates = async () => {
      setLoadingStates(true);
      try {
        const response = await axios.get(`${BaseUrl.baseurl}States`, {
          headers: { accept: 'text/plain' }
        });
        setStates(response.data || []);
      } catch (error) {
        console.error('Error fetching states:', error);
        setError(t('failedToLoadStates'));
      } finally {
        setLoadingStates(false);
      }
    };
    fetchStates();
  }, [t]);

  useEffect(() => {
    const fetchDistrictsByState = async () => {
      if (!formData.stateCode) {
        setDistricts([]);
        return;
      }
      setLoadingDistricts(true);
      try {
        const response = await axios.get(
          `${BaseUrl.baseurl}Districts/GetDistrictsByStateId?StateCode=${formData.stateCode}`,
          { headers: { accept: 'text/plain' } }
        );
        setDistricts(response.data || []);
        if (formData.districtCode) {
          const exists = response.data.some(d => d.districtCode === formData.districtCode);
          if (!exists) {
            setFormData(prev => ({ ...prev, districtCode: '' }));
          }
        }
      } catch (error) {
        console.error('Error fetching districts:', error);
        setError(t('failedToLoadDistricts'));
      } finally {
        setLoadingDistricts(false);
      }
    };
    fetchDistrictsByState();
  }, [formData.stateCode, t]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      if (name === 'stateCode') {
        updatedData.districtCode = '';
      }
      return updatedData;
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    if (!formData.stateCode) {
      setError(t('stateRequired'));
      setSubmitting(false);
      return;
    }
    if (!formData.districtCode) {
      setError(t('districtRequired'));
      setSubmitting(false);
      return;
    }
    if (!formData.cityVillageNameEn.trim()) {
      setError(t('cityVillageNameRequired', 'City/Village name is required'));
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        cityVillageCode: formData.cityVillageCode || null,
        cityVillageName: formData.cityVillageNameEn,
        hindi: formData.cityVillageNameHi.trim() || null,
        districtCode: formData.districtCode,
        status: formData.status,
        district: null
      };

      console.log('Submitting payload:', payload);

      const response = await axios.post(
        `${BaseUrl.baseurl}CityVillages`,
        payload,
        {
          headers: {
            accept: 'text/plain',
            'Content-Type': 'application/json',
          }
        }
      );
      toast.success("City/Village added successfully!", {
        style: {
          background: '#ffffff',
          color: '#1f2937',
          border: '1px solid #e5e7eb'
        }
      });
      if (response.status === 200 || response.status === 201) {
        alert(t('cityVillageAdded'));
        setFormData({
          cityVillageCode: '',
          cityVillageNameEn: '',
          cityVillageNameHi: '',
          stateCode: '',
          districtCode: '',
          status: true
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(t('saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/route-finder-panel');
  };

  return (
    <div className="add-region-container">
      <ToastContainer/>
      <div className="form-header">
        <button 
          className="btn-back" 
          onClick={handleCancel}
          disabled={submitting}
        >
          ← {t('back', 'Back')}
        </button>
        <h2>{t('addNewCityVillage', 'Add New City/Village')}</h2>
        <p>{t('createCityVillageDescription', 'Create new city or village entries for route planning')}</p>
      </div>

      {error && (
        <div className="alert alert-error">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="region-form">
        <div className="form-grid">
          {/* Basic Information */}
          <div className="form-section">
            <h3 className="section-title">
              <FiMap className="section-icon" />
              {t('cityVillageInformation', 'City/Village Information')}
            </h3>
            
            <div className="input-group">
              <label htmlFor="cityVillageNameEn">
                {t('cityVillageNameLabel', 'City/Village Name')} *
              </label>
              <input
                type="text"
                id="cityVillageNameEn"
                name="cityVillageNameEn"
                value={formData.cityVillageNameEn}
                onChange={handleInputChange}
                placeholder={t('cityVillageNamePlaceholder', 'e.g., Mysuru City Center')}
                required
                disabled={submitting}
                maxLength="100"
              />
              <small className="input-hint">
                {t('cityVillageNameHint', 'Enter the full name of the city or village')}
              </small>
            </div>
            
            <div className="input-group">
              <label htmlFor="cityVillageNameHi">
                {t('hindiName')}
              </label>
              <input
                type="text"
                id="cityVillageNameHi"
                name="cityVillageNameHi"
                value={formData.cityVillageNameHi}
                onChange={handleInputChange}
                placeholder={t('hindiNamePlaceholder')}
                disabled={submitting}
                maxLength="100"
              />
              <small className="input-hint">{t('hindiNameHint')}</small>
            </div>

            <div className="input-row">
              <div className="input-group">
                <label htmlFor="cityVillageCode">
                  {t('cityVillageCode', 'City/Village Code')}
                </label>
                <input
                  type="text"
                  id="cityVillageCode"
                  name="cityVillageCode"
                  value={formData.cityVillageCode}
                  onChange={handleInputChange}
                  placeholder={t('cityVillageCodePlaceholder', 'e.g., MYC001')}
                  disabled={submitting}
                  maxLength="20"
                />
                <small className="input-hint">
                  {t('uniqueIdentifierHint', 'Optional unique identifier code')}
                </small>
              </div>
            </div>
          </div>

          {/* Location Information */}
          <div className="form-section">
            <h3 className="section-title">
              <FiGlobe className="section-icon" />
              {t('locationInformation', 'Location Information')}
            </h3>
            
            <div className="input-row">
              <div className="input-group">
                <label htmlFor="stateCode">
                  {t('state', 'State')} *
                  {loadingStates && <span className="loading-indicator"><div className="spinner-tiny"></div></span>}
                </label>
                <select
                  id="stateCode"
                  name="stateCode"
                  value={formData.stateCode}
                  onChange={handleInputChange}
                  required
                  disabled={loadingStates || submitting}
                >
                  <option value="">{t('selectState', 'Select State')}</option>
                  {states.map(state => (
                    <option key={state.stateCode} value={state.stateCode}>
                      {state.stateName}
                    </option>
                  ))}
                </select>
                {loadingStates && (
                  <small className="input-hint">{t('loadingStates', 'Loading states...')}</small>
                )}
              </div>

              <div className="input-group">
                <label htmlFor="districtCode">
                  {t('district', 'District')} *
                  {loadingDistricts && <span className="loading-indicator"><div className="spinner-tiny"></div></span>}
                </label>
                <select
                  id="districtCode"
                  name="districtCode"
                  value={formData.districtCode}
                  onChange={handleInputChange}
                  required
                  disabled={loadingDistricts || submitting || !formData.stateCode}
                >
                  <option value="">{t('selectDistrict', 'Select District')}</option>
                  {districts.map(district => (
                    <option key={district.districtCode} value={district.districtCode}>
                      {district.districtName}
                    </option>
                  ))}
                </select>
                {loadingDistricts && (
                  <small className="input-hint">{t('loadingDistricts', 'Loading districts...')}</small>
                )}
                {formData.stateCode && districts.length === 0 && !loadingDistricts && (
                  <small className="input-hint">
                    {t('noDistrictsFound', 'No districts found for selected state')}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Status Section - Optional (commented out) */}
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleCancel}
            disabled={submitting}
          >
            <FiX /> {t('cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting || loadingStates || loadingDistricts}
          >
            {submitting ? (
              <>
                <div className="spinner-small"></div>
                {t('creating', 'Creating...')}
              </>
            ) : (
              <>
                <FiPlus /> {t('createCityVillage', 'Create City/Village')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddCityVillage;